import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API, absoluteUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import StatusBadge from "@/components/StatusBadge";
import MapPreview from "@/components/MapPreview";
import SignaturePad from "@/components/SignaturePad";
import { HardHat, ShieldCheck, XCircle, HelpCircle, FileText, CheckCircle2, Image as ImageIcon, Map as MapIcon, HelpingHand, FileDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const DECISION_BUTTON_CLASSES = {
  approve: "h-12 bg-emerald-600 hover:bg-emerald-700",
  reject: "h-12 bg-red-600 hover:bg-red-700",
  more_info: "h-12 bg-purple-600 hover:bg-purple-700",
};

export default function ClientReview({ reviewKind = "hold_point" }) {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [decision, setDecision] = useState(null);
  const [form, setForm] = useState({
    reviewer_name: "", company: "", role: "Superintendent", comments: "",
    rejection_reason: "", corrective_action: "", missing_evidence: "",
  });
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  const [signatureMode, setSignatureMode] = useState("drawn"); // "drawn" | "typed"
  const [pkgDownloading, setPkgDownloading] = useState(false);

  const isAP = reviewKind === "approval_point";
  const reviewBasePath = isAP ? "approval-point-review" : "hold-point-review";

  useEffect(() => {
    axios.get(`${API}/public/${reviewBasePath}/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Link invalid or expired"));
  }, [token, reviewBasePath]);

  const submit = async () => {
    if (!decision) { toast.error("Choose a decision"); return; }
    if (!form.reviewer_name) { toast.error("Reviewer name required"); return; }
    if (!form.company) { toast.error("Company required"); return; }
    if (decision === "approve" && !confirm) { toast.error(isAP ? "Please confirm approval" : "Please confirm release"); return; }
    if (decision === "reject" && !form.rejection_reason) { toast.error("Rejection reason required"); return; }
    if (decision === "more_info" && !form.comments) { toast.error("Comment required"); return; }
    if (signatureMode === "drawn" && !signatureImage) { toast.error("Please sign in the signature box, or switch to typed sign-off"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/${reviewBasePath}/${token}/decision`, {
        decision, ...form, confirm,
        signature_image_b64: signatureMode === "drawn" ? signatureImage : null,
        signature_method: signatureMode,
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  const downloadPackage = async () => {
    setPkgDownloading(true);
    try {
      const r = await axios.get(`${API}/public/review-package/${token}/package.zip`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      let fname = (m ? m[1] : `${data?.lot?.lot_number || "Lot"}_Review_Package.zip`).replace(/[^\w. -]+/g, "_");
      if (!/\.zip$/i.test(fname)) fname += ".zip";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Review package downloaded.");
    } catch {
      toast.error("Package download failed");
    } finally { setPkgDownloading(false); }
  };

  if (err) return <div className="flex min-h-screen items-center justify-center p-8 text-center"><div><div className="font-heading text-2xl font-bold text-red-700">{err}</div><div className="mt-1 text-slate-500">This review link is invalid or has been already used.</div></div></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center text-slate-500">Loading review…</div>;

  const { lot, project, hold_point: hpData, approval_point: apData, linked_checklist_items, evidence, ncrs, review, plan, markups, rfis, generated_itps } = data;
  // Unified point object — works for both HP & AP
  const point = isAP ? apData : hpData;
  const pointLabel = isAP ? "Action Point" : "Hold Point";
  const pageTitle = isAP ? "Action Point Review" : "Hold Point Release Review";
  const approveBtnLabel = isAP ? "Approve Action Point" : "Approve / Release Hold Point";
  const confirmCheckboxLabel = isAP
    ? "I confirm this approval point is hereby approved and recorded."
    : "I confirm this hold point is approved and may proceed.";

  if (done || review.decision) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicHeader/>
        <div className="mx-auto max-w-2xl p-6">
          <Card className="border-slate-200 p-8 text-center" data-testid="review-complete">
            <CheckCircle2 className="mx-auto size-12 text-emerald-600"/>
            <h1 className="mt-3 font-heading text-2xl font-black tracking-tighter">Decision Recorded</h1>
            <p className="mt-1 text-slate-600">Your decision has been logged in the audit trail.</p>
            <div className="mt-4 inline-block"><StatusBadge status={review.status}/></div>
            <div className="mt-6 rounded-md bg-slate-100 p-4 text-left text-sm">
              <Row k="Project" v={project.name}/>
              <Row k="Lot" v={lot.lot_number}/>
              <Row k={pointLabel} v={`${point.hp_number} — ${point.title}`}/>
              <Row k="Decision" v={review.decision || decision}/>
            </div>
            <p className="mt-4 text-xs text-slate-500">You can close this tab safely.</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid={isAP ? "client-ap-review-page" : "client-review-page"}>
      <PublicHeader/>
      <div className="mx-auto max-w-4xl p-4 md:p-8">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="overline mb-1 text-teal-700">{lot.lot_number} · {lot.itp_number}</div>
            <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl" data-testid="review-page-title">{pageTitle}</h1>
            <p className="mt-1 text-slate-600">
              {isAP
                ? "Review the submitted evidence and provide your approval decision below."
                : "Review the submitted evidence and provide your digital sign-off below."}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={downloadPackage}
            disabled={pkgDownloading}
            className="shrink-0 border-teal-700 text-teal-700 hover:bg-teal-50"
            data-testid="client-download-package-btn"
          >
            <FileDown className="mr-2 size-4"/>{pkgDownloading ? "Preparing package…" : "Download Review Package (ZIP)"}
          </Button>
        </div>

        <Card className="border-slate-200 p-5">
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <Row k="Project" v={project.name}/>
            <Row k="Lot Number" v={lot.lot_number}/>
            <Row k="Activity" v={lot.activity}/>
            <Row k="Location / Chainage" v={lot.location || "—"}/>
            <Row k="ITP Reference" v={`${lot.itp_number} — ${lot.itp_name}`}/>
            <Row k={pointLabel} v={`${point.hp_number} — ${point.title}`}/>
            <Row k="Submitted By" v={review.submitted_by_name}/>
            <Row k="Submitted Date/Time" v={new Date(review.submitted_at).toLocaleString()}/>
          </div>
          {point.description && <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm text-slate-700"><b>Description:</b> {point.description}</div>}
          {review.engineer_comments && <div className="mt-2 rounded-md bg-sky-50 p-3 text-sm"><b>Engineer Comments:</b> {review.engineer_comments}</div>}
        </Card>

        {plan && (
          <>
            <SectionTitle icon={MapIcon} title="Lot Map (read-only)" count={null}/>
            <Card className="border-slate-200 p-3" data-testid="client-map-card">
              <div className="overline text-teal-700">{plan.drawing_number} · Rev {plan.revision}</div>
              <div className="mt-0.5 font-heading text-base font-bold">{plan.title}</div>
              <div className="text-xs text-slate-600">{plan.discipline}</div>
              <div className="mt-3">
                <MapPreview planId={plan.id} markups={markups || []} token={token}/>
              </div>
              <p className="mt-2 text-[11px] text-slate-500">Marked-up lot boundary and hold-point location. You can pinch / scroll to zoom on the image.</p>
            </Card>
          </>
        )}

        <SectionTitle icon={CheckCircle2} title="Linked ITP Items" count={linked_checklist_items.length}/>
        <Card className="border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
              <tr><th className="px-3 py-2">Item</th><th className="px-3 py-2">Section</th><th className="px-3 py-2">Status</th></tr>
            </thead>
            <tbody>
              {linked_checklist_items.map(i => (
                <tr key={i.id} className="border-t border-slate-100"><td className="px-3 py-2">{i.item_description}</td><td className="px-3 py-2 text-slate-600">{i.section_name}</td><td className="px-3 py-2"><StatusBadge status={i.status}/></td></tr>
              ))}
              {linked_checklist_items.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-center text-slate-500">No linked items</td></tr>}
            </tbody>
          </table>
        </Card>

        <SectionTitle icon={ImageIcon} title="Uploaded Evidence" count={evidence.length}/>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {evidence.map(e => <PublicEvidence key={e.id} item={e} token={token}/>)}
          {evidence.length === 0 && <div className="col-span-full rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">No evidence attached.</div>}
        </div>

        {ncrs.length > 0 && (
          <>
            <SectionTitle icon={XCircle} title="Linked NCRs" count={ncrs.length}/>
            <Card className="border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                  <tr><th className="px-3 py-2">NCR</th><th className="px-3 py-2">Issue</th><th className="px-3 py-2">Status</th></tr>
                </thead>
                <tbody>
                  {ncrs.map(n => <tr key={n.id} className="border-t border-slate-100"><td className="px-3 py-2 font-mono">{n.ncr_number}</td><td className="px-3 py-2">{n.issue}</td><td className="px-3 py-2"><StatusBadge status={n.status}/></td></tr>)}
                </tbody>
              </table>
            </Card>
          </>
        )}

        {rfis && rfis.length > 0 && (
          <>
            <SectionTitle icon={HelpingHand} title="Linked RFIs (read-only)" count={rfis.length}/>
            <div className="space-y-2" data-testid="client-rfis">
              {rfis.map(r => (
                <Card key={r.id} className="border-slate-200 p-3" data-testid={`client-rfi-${r.rfi_number}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-sm font-bold text-teal-700">{r.rfi_number}</div>
                    <StatusBadge status={r.status}/>
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">{r.title}</div>
                  {r.description && <div className="mt-1 text-xs text-slate-700"><b>Question:</b> {r.description}</div>}
                  {r.response_summary && <div className="mt-1 rounded bg-emerald-50 p-2 text-xs text-emerald-900"><b>Response:</b> {r.response_summary}</div>}
                  <div className="mt-1 text-[11px] text-slate-500">
                    Raised by {r.raised_by_name} · {r.date_raised && new Date(r.date_raised).toLocaleDateString()}
                    {r.response_required_by && <> · Response required by {new Date(r.response_required_by).toLocaleDateString()}</>}
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {generated_itps && generated_itps.length > 0 && (
          <>
            <SectionTitle icon={FileDown} title="Completed ITP (read-only download)" count={generated_itps.length}/>
            <div className="space-y-2" data-testid="client-completed-itps">
              {generated_itps.map((o) => (
                <Card key={o.id} className="border-slate-200 p-3" data-testid={`client-completed-itp-${o.id}`}>
                  <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900" title={o.filename}>{o.filename}</div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        Generated by {o.generated_by_name} · {new Date(o.generated_at).toLocaleString()}
                      </div>
                    </div>
                    <a
                      href={`${API}/generated-itps/${o.id}/download?token=${token}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center rounded-md border border-teal-700 px-3 py-2 text-xs font-semibold text-teal-700 hover:bg-teal-50"
                      data-testid={`client-download-completed-itp-${o.id}`}
                    >
                      <FileDown className="mr-1.5 size-4"/> Download .docx
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        <SectionTitle icon={ShieldCheck} title="Your Decision" count={null}/>
        {(() => {
          const reviewable = isAP
            ? (point.status === "Awaiting Approval" || point.status === "Under Review" || point.status === "Submitted")
            : (point.status === "Awaiting Client Review" || point.status === "Under Review");
          if (!reviewable) {
            const ar = point.approval_record;
            return (
              <Card className="border-slate-200 p-5" data-testid="review-outcome-readonly">
                <div className="flex items-center gap-2">
                  <StatusBadge status={point.status}/>
                  <span className="text-sm text-slate-600">
                    {isAP
                      ? "No further decision is required for this approval point."
                      : "No further decision is required for this hold point."}
                  </span>
                </div>
                {ar && (
                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                    <div><div className="overline">Reviewer</div><div className="font-semibold">{ar.reviewer_name}</div></div>
                    <div><div className="overline">Company</div><div className="font-semibold">{ar.company || "—"}</div></div>
                    <div><div className="overline">Role</div><div className="font-semibold">{ar.role || "—"}</div></div>
                    <div><div className="overline">Decision</div><div className="font-semibold">{ar.decision}</div></div>
                    <div><div className="overline">Timestamp</div><div className="font-semibold">{ar.timestamp && new Date(ar.timestamp).toLocaleString()}</div></div>
                    <div className="md:col-span-2"><div className="overline">Comments</div><div>{ar.comments || ar.rejection_reason || ar.missing_evidence || "—"}</div></div>
                  </div>
                )}
              </Card>
            );
          }
          return (
            <Card className="border-slate-200 p-5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <DecisionBtn active={decision === "approve"} color="emerald" onClick={() => setDecision("approve")} icon={ShieldCheck} title={isAP ? "Approve Action Point" : "Approve / Release"} subtitle={isAP ? "Record the approval decision" : "Allow construction to proceed"} testid="decision-approve"/>
                <DecisionBtn active={decision === "reject"} color="red" onClick={() => setDecision("reject")} icon={XCircle} title="Reject" subtitle="Does not meet requirements" testid="decision-reject"/>
                <DecisionBtn active={decision === "more_info"} color="purple" onClick={() => setDecision("more_info")} icon={HelpCircle} title="Request More Information" subtitle="Need additional evidence" testid="decision-more-info"/>
              </div>

              {decision && (
                <div className="mt-5 space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div><Label>Reviewer Name *</Label><Input value={form.reviewer_name} onChange={e=>setForm({...form,reviewer_name:e.target.value})} data-testid="reviewer-name" className="h-11"/></div>
                    <div><Label>Company *</Label><Input value={form.company} onChange={e=>setForm({...form,company:e.target.value})} data-testid="reviewer-company" className="h-11"/></div>
                    <div><Label>Role / Title</Label><Input value={form.role} onChange={e=>setForm({...form,role:e.target.value})} data-testid="reviewer-role" className="h-11"/></div>
                  </div>
                  <div><Label>Comments {decision === "more_info" && "*"}</Label><Textarea value={form.comments} onChange={e=>setForm({...form,comments:e.target.value})} data-testid="reviewer-comments"/></div>
                  {decision === "reject" && (
                    <>
                      <div><Label>Rejection Reason *</Label><Textarea value={form.rejection_reason} onChange={e=>setForm({...form,rejection_reason:e.target.value})} data-testid="reject-reason"/></div>
                      <div><Label>Corrective Action Required</Label><Textarea value={form.corrective_action} onChange={e=>setForm({...form,corrective_action:e.target.value})} data-testid="reject-correction"/></div>
                    </>
                  )}
                  {decision === "more_info" && (
                    <div><Label>Missing Evidence</Label><Textarea value={form.missing_evidence} onChange={e=>setForm({...form,missing_evidence:e.target.value})} data-testid="more-info-missing"/></div>
                  )}

                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Electronic signature *</Label>
                      <div className="text-xs">
                        <button type="button" data-testid="signature-mode-drawn" onClick={() => { setSignatureMode("drawn"); }} className={signatureMode === "drawn" ? "font-semibold text-teal-700" : "text-slate-500 hover:underline"}>Draw</button>
                        <span className="mx-2 text-slate-300">|</span>
                        <button type="button" data-testid="signature-mode-typed" onClick={() => { setSignatureMode("typed"); setSignatureImage(null); }} className={signatureMode === "typed" ? "font-semibold text-teal-700" : "text-slate-500 hover:underline"}>Typed sign-off (fallback)</button>
                      </div>
                    </div>
                    {signatureMode === "drawn" ? (
                      <SignaturePad value={signatureImage} onChange={setSignatureImage} />
                    ) : (
                      <div className="rounded-md border border-slate-300 bg-slate-50 p-3 text-sm" data-testid="signature-typed-block">
                        <div className="font-semibold text-slate-900">{form.reviewer_name || "—"}</div>
                        <div className="text-slate-600">{form.company || "—"}{form.role ? ` · ${form.role}` : ""}</div>
                        <div className="mt-2 text-xs text-slate-500">By submitting this decision you are electronically signing this {pointLabel.toLowerCase()} record.</div>
                      </div>
                    )}
                  </div>

                  {decision === "approve" && (
                    <label className="flex cursor-pointer items-start gap-2 rounded-md bg-emerald-50 p-3 text-sm">
                      <Checkbox checked={confirm} onCheckedChange={setConfirm} data-testid="approve-confirm" className="mt-0.5"/>
                      <span>{confirmCheckboxLabel}</span>
                    </label>
                  )}
                  <div className="flex justify-end">
                    <Button disabled={submitting} onClick={submit} className={DECISION_BUTTON_CLASSES[decision]} data-testid="submit-decision">
                      {submitting ? "Submitting…" : (decision === "approve" ? approveBtnLabel : "Submit Decision")}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          );
        })()}
        <div className="my-8 text-center text-xs text-slate-400">FlowQA — Civil Construction QA · Digital sign-off & audit trail</div>
      </div>
    </div>
  );
}

function PublicHeader() {
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/organisation/branding`)
      .then(r => setBranding(r.data))
      .catch(() => {});
  }, []);
  return (
    <header className="border-b border-slate-200 bg-white" data-testid="public-header">
      <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
        {branding?.logo_file_url ? (
          <img
            src={absoluteUrl(branding.logo_file_url)}
            alt={branding.company_name || "Company logo"}
            className="h-9 max-w-[160px] object-contain"
            data-testid="public-company-logo"
          />
        ) : (
          <div className="grid size-8 place-items-center rounded-md bg-teal-700 text-white"><HardHat className="size-4"/></div>
        )}
        {branding?.company_name ? (
          <>
            <div className="font-heading text-base font-bold tracking-tight text-slate-900" data-testid="public-company-name">{branding.company_name}</div>
            <div className="ml-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Powered by FlowQA</div>
          </>
        ) : (
          <div className="font-heading text-lg font-black tracking-tighter">FlowQA</div>
        )}
        <span className="ml-auto rounded bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-orange-700">Client Review</span>
      </div>
    </header>
  );
}

function SectionTitle({ icon: Icon, title, count }) {
  return (
    <div className="mb-2 mt-6 flex items-center gap-2">
      <Icon className="size-4 text-teal-700"/>
      <h2 className="font-heading text-lg font-bold tracking-tight">{title}</h2>
      {count !== null && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{count}</span>}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex flex-col sm:flex-row sm:gap-2"><span className="overline shrink-0 sm:w-44">{k}</span><span className="font-medium text-slate-900">{v}</span></div>;
}

function DecisionBtn({ active, color, onClick, icon: Icon, title, subtitle, testid }) {
  const palettes = {
    emerald: active ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "hover:border-emerald-300",
    red: active ? "border-red-500 bg-red-50 text-red-800" : "hover:border-red-300",
    purple: active ? "border-purple-500 bg-purple-50 text-purple-800" : "hover:border-purple-300",
  };
  return (
    <button data-testid={testid} onClick={onClick} className={`rounded-md border-2 p-4 text-left transition-colors ${active ? palettes[color] : "border-slate-200 " + palettes[color]}`}>
      <Icon className="size-6"/>
      <div className="mt-2 font-heading text-base font-bold">{title}</div>
      <div className="text-xs text-slate-600">{subtitle}</div>
    </button>
  );
}

function PublicEvidenceThumb({ isImage, url, item }) {
  if (isImage && url) {
    return <img src={url} alt={item.file_name} className="size-full object-cover" />;
  }
  if (isImage) {
    return (
      <div className="grid h-full place-items-center">
        <ImageIcon className="size-10 text-slate-400" />
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col items-center justify-center text-slate-500">
      <FileText className="size-10" />
      <span className="mt-1 text-[10px] uppercase">{item.file_type?.split("/").pop()}</span>
    </div>
  );
}

function PublicEvidence({ item, token }) {
  const [url, setUrl] = useState(null);
  const isImage = item.file_type?.startsWith("image/");
  useEffect(() => {
    if (!isImage) return;
    let cancel = false;
    axios.get(`${API}/evidence/${item.id}/download?token=${token}`, { responseType: "blob" })
      .then(r => { if (!cancel) setUrl(URL.createObjectURL(r.data)); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [item.id, token, isImage]);
  const open = () => window.open(`${API}/evidence/${item.id}/download?token=${token}`, "_blank");
  return (
    <Card className="overflow-hidden border-slate-200" data-testid={`public-evidence-${item.id}`}>
      <div className="aspect-square bg-slate-100">
        <PublicEvidenceThumb isImage={isImage} url={url} item={item} />
      </div>
      <div className="space-y-1 p-2">
        <div className="overline">{item.category}</div>
        <div className="truncate text-xs font-semibold" title={item.file_name}>{item.file_name}</div>
        <button onClick={open} className="text-[10px] font-semibold text-teal-700">Open / Download</button>
      </div>
    </Card>
  );
}
